import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d664v4s8e72clamqh8s0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus(''), 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md border-b border-black/5' : 'bg-transparent'
      }`}>
        <div className="w-full px-20">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/3dd9d98c-3753-493e-83ba-7ff55a6fda3b.png" 
                alt="POWER d.o.o. Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-sm font-light tracking-wider text-[#2A2A2A] uppercase">POWER d.o.o.</span>
            </div>
            <div className="flex gap-12">
              <a href="#home" className="text-sm text-[#2A2A2A] hover:text-[#FF8A75] transition-colors cursor-pointer whitespace-nowrap">Home</a>
              <a href="#products" className="text-sm text-[#2A2A2A] hover:text-[#FF8A75] transition-colors cursor-pointer whitespace-nowrap">Products</a>
              <a href="#about" className="text-sm text-[#2A2A2A] hover:text-[#FF8A75] transition-colors cursor-pointer whitespace-nowrap">About</a>
              <a href="#contact" className="text-sm text-[#2A2A2A] hover:text-[#FF8A75] transition-colors cursor-pointer whitespace-nowrap">Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-20">
        <div className="flex items-center gap-16">
          {/* Left Content */}
          <div className="w-2/5 pl-8">
            <div className="inline-flex items-center px-5 py-2 rounded-full bg-[#F0EDFF] mb-10">
              <span className="text-xs font-medium text-[#2A2A2A]">iOS Development Experts</span>
            </div>
            <h1 className="mb-10">
              <div className="text-6xl font-normal text-[#1A2B4A] leading-tight mb-2">Crafting Premium</div>
              <div className="text-6xl italic font-serif text-[#1A2B4A] leading-tight mb-2" style={{ fontFamily: 'Georgia, serif' }}>iOS Experiences</div>
              <div className="text-6xl font-normal text-[#1A2B4A] leading-tight">Since 2020</div>
            </h1>
            <p className="text-base text-[#6B7280] leading-relaxed max-w-md">
              A team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
            </p>
          </div>

          {/* Right Visual */}
          <div className="w-3/5 relative">
            <div className="rounded-[32px] overflow-hidden relative" style={{ height: '600px' }}>
              <img 
                src="https://public.readdy.ai/ai/img_res/6f7ea1b3336bcd3a7831475b0ef55d06.jpg" 
                alt="iOS App Interface" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#E8E4FF]/30 via-transparent to-[#E4F0FF]/30"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 px-20">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF6B58] mb-6">
            <i className="ri-shield-check-line text-white text-2xl"></i>
          </div>
          <h2 className="text-5xl font-bold text-[#1A2B4A]">Our Product Ecosystem</h2>
        </div>

        {/* Product Grid */}
        <div className="space-y-6">
          {/* Row 1 */}
          <div className="grid grid-cols-3 gap-6">
            {/* VPN Card */}
            <div className="bg-[#F5F7FA] rounded-[32px] p-12 relative">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A90E2] to-[#357ABD] flex items-center justify-center mb-8">
                <i className="ri-shield-keyhole-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-[#1A2B4A] mb-5">VPN Pro</h3>
              <p className="text-[#6B7280] text-base leading-relaxed mb-8">
                Military-grade encryption protecting your online privacy. Secure browsing with lightning-fast servers across 50+ countries.
              </p>
              <div className="absolute bottom-8 right-8 px-4 py-2 bg-white rounded-full">
                <span className="text-xs font-medium text-[#2A2A2A]">iOS 15+</span>
              </div>
            </div>

            {/* Feature Highlight */}
            <div className="bg-[#1E2A3A] rounded-[32px] p-12 flex flex-col items-center justify-center relative overflow-hidden">
              <div className="w-64 h-64 flex items-center justify-center mb-8">
                <i className="ri-lock-password-line text-[#C4FF61] text-9xl"></i>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-2xl font-bold text-white">Military-Grade Encryption</h3>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="rounded-[32px] overflow-hidden relative" style={{ height: '400px' }}>
              <img 
                src="https://readdy.ai/api/search-image?query=iPhone%20screen%20displaying%20modern%20cleaner%20app%20interface%20with%20storage%20optimization%20features%20clean%20minimalist%20UI%20design%20showing%20file%20management%20and%20memory%20cleanup%20tools%20professional%20iOS%20application%20screenshot%20light%20theme%20with%20progress%20indicators%20and%20statistics&width=400&height=400&seq=cleaner-screenshot-001&orientation=squarish" 
                alt="Cleaner Pro App" 
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-8 left-8">
                <h3 className="text-2xl font-bold text-white mb-2">Cleaner Pro</h3>
                <p className="text-white/90 text-sm">Storage Optimization</p>
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-[#F5F7FA] rounded-[32px] p-10 relative">
              <div className="text-center">
                <i className="ri-double-quotes-l text-[#1A2B4A] text-4xl mb-4"></i>
                <p className="text-lg font-medium text-[#1A2B4A] mb-4">One-Tap Optimization</p>
                <p className="text-sm text-[#6B7280]">Instantly free up storage and boost performance with a single tap.</p>
                <div className="absolute bottom-6 right-6 px-4 py-2 bg-white rounded-full">
                  <span className="text-sm font-medium text-[#2A2A2A]">4.8★</span>
                </div>
              </div>
            </div>
            <div className="bg-[#F5F7FA] rounded-[32px] p-10 relative">
              <div className="text-center">
                <i className="ri-double-quotes-r text-[#1A2B4A] text-4xl mb-4"></i>
                <p className="text-lg font-medium text-[#1A2B4A] mb-4">Privacy First Design</p>
                <p className="text-sm text-[#6B7280]">Your data stays on your device. No tracking, no compromises.</p>
                <div className="absolute bottom-6 right-6 px-3 py-1 bg-white rounded-full">
                  <span className="text-xs font-medium text-[#2A2A2A]">App Store Featured</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-20">
        <div className="flex items-center gap-20">
          {/* Left Title */}
          <div className="w-2/5">
            <p className="text-sm text-[#6B7280] font-serif mb-6" style={{ fontFamily: 'Georgia, serif' }}>Est. 2020</p>
            <h2 className="mb-8">
              <div className="text-5xl font-normal text-[#1A2B4A] leading-tight mb-2">Building the Future</div>
              <div className="text-5xl italic font-serif text-[#1A2B4A] leading-tight mb-2" style={{ fontFamily: 'Georgia, serif' }}>of Mobile</div>
              <div className="text-5xl font-normal text-[#1A2B4A] leading-tight">Security</div>
            </h2>
            <p className="text-base text-[#6B7280]">Based in Sarajevo</p>
          </div>

          {/* Right Description */}
          <div className="w-3/5">
            <div className="w-4/5">
              <p className="text-lg text-[#6B7280] leading-relaxed mb-6">
                POWER d.o.o. is a dedicated team of iOS development experts committed to creating applications that make a real difference in users' daily lives. Our focus on security and optimization stems from a deep understanding of modern mobile challenges.
              </p>
              <p className="text-lg text-[#6B7280] leading-relaxed mb-6">
                With our flagship products—VPN Pro and Cleaner Pro—we've helped over 2 million users worldwide protect their privacy and optimize their device performance. Every line of code we write is guided by principles of simplicity, security, and user-centric design.
              </p>
              <p className="text-lg text-[#6B7280] leading-relaxed">
                Our development philosophy combines cutting-edge technology with intuitive interfaces, ensuring that powerful features remain accessible to everyone. We believe that security and performance shouldn't come at the cost of usability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-20 bg-[#0A2F3A] relative overflow-hidden">
        {/* Background Map Pattern */}
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" viewBox="0 0 1200 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 200 Q 200 150, 300 200 T 500 200 T 700 200 T 900 200 T 1100 200" stroke="white" strokeWidth="2"/>
            <path d="M150 300 Q 250 250, 350 300 T 550 300 T 750 300 T 950 300" stroke="white" strokeWidth="2"/>
            <circle cx="300" cy="200" r="4" fill="white"/>
            <circle cx="500" cy="200" r="4" fill="white"/>
            <circle cx="700" cy="200" r="4" fill="white"/>
            <circle cx="350" cy="300" r="4" fill="white"/>
            <circle cx="550" cy="300" r="4" fill="white"/>
          </svg>
        </div>

        <div className="relative z-10">
          {/* Upper Footer Grid */}
          <div className="grid grid-cols-5 gap-16 mb-20">
            {/* Brand Column */}
            <div className="col-span-1">
              <div className="flex items-center gap-2 mb-12">
                <img 
                  src="https://public.readdy.ai/ai/img_res/3dd9d98c-3753-493e-83ba-7ff55a6fda3b.png" 
                  alt="POWER d.o.o. Logo" 
                  className="h-8 w-8 object-contain"
                />
                <h3 className="text-2xl font-serif text-white" style={{ fontFamily: 'Georgia, serif' }}>
                  POWER d.o.o.
                </h3>
              </div>
              
              <div className="mb-10">
                <h4 className="text-3xl font-serif text-white leading-tight mb-2" style={{ fontFamily: 'Georgia, serif' }}>Stay Updated with</h4>
                <h4 className="text-3xl font-serif text-white leading-tight" style={{ fontFamily: 'Georgia, serif' }}>Latest Features</h4>
              </div>
            </div>

            {/* Products Column */}
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Products</h4>
              <ul className="space-y-4">
                <li><span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">VPN Pro</span></li>
                <li><span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Cleaner Pro</span></li>
                <li><span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Premium Bundle</span></li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Company</h4>
              <ul className="space-y-4">
                <li><a href="#about" className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">About Us</a></li>
                <li><span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Our Team</span></li>
                <li><span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Careers</span></li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Resources</h4>
              <ul className="space-y-4">
                <li><span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Documentation</span></li>
                <li><span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Support Center</span></li>
                <li><span className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Privacy Policy</span></li>
              </ul>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-base font-semibold text-white mb-6">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-white/80 text-base mt-1"></i>
                  <div>
                    <p className="text-sm text-white/80">Address</p>
                    <p className="text-sm text-white">Plandiste do broja 16</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-building-line text-white/80 text-base mt-1"></i>
                  <div>
                    <p className="text-sm text-white/80">City</p>
                    <p className="text-sm text-white">Sarajevo</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-mail-line text-white/80 text-base mt-1"></i>
                  <div>
                    <p className="text-sm text-white/80">Zip</p>
                    <p className="text-sm text-white">71000</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-global-line text-white/80 text-base mt-1"></i>
                  <div>
                    <p className="text-sm text-white/80">Country</p>
                    <p className="text-sm text-white">Bosnia & Herzegovina</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto mb-20">
            <h3 className="text-3xl font-bold text-white text-center mb-12">Get in Touch</h3>
            <form onSubmit={handleSubmit} data-readdy-form id="contact_form" className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#FF8A75] transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email"
                  required
                  className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#FF8A75] transition-colors"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#FF8A75] transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-white/50 mt-2">{formData.message.length}/500 characters</p>
              </div>
              <button
                type="submit"
                disabled={formStatus === 'sending'}
                className="w-full px-8 py-4 bg-[#FF8A75] text-white font-medium rounded-lg hover:bg-[#FF7A65] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
              {formStatus === 'success' && (
                <p className="text-[#C4FF61] text-center text-sm">Message sent successfully!</p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-400 text-center text-sm">Failed to send message. Please try again.</p>
              )}
            </form>
          </div>

          {/* Lower Footer Bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex items-center justify-between">
              {/* Copyright */}
              <p className="text-sm text-white/70">© 2025 POWER d.o.o. All rights reserved</p>

              {/* Social Icons */}
              <div className="flex items-center gap-5">
                <div className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-white/60 transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-white text-lg"></i>
                </div>
                <div className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-white/60 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-white text-lg"></i>
                </div>
                <div className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-white/60 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-white text-lg"></i>
                </div>
                <div className="w-10 h-10 border border-white/30 rounded-lg flex items-center justify-center hover:border-white/60 transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-white text-lg"></i>
                </div>
              </div>

              {/* Contact Info */}
              <div className="text-right">
                <p className="text-sm text-white mb-2">
                  <i className="ri-mail-line mr-2"></i>
                  help@power-doo.com
                </p>
                <p className="text-sm text-white">
                  <i className="ri-phone-line mr-2"></i>
                  +1 2762925723
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
